import {
  type Cell,
  type Column,
  type ColumnDef,
  createColumnHelper,
  type DisplayColumnDef,
  type ExpandedState,
  FlexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  type GroupingState,
  type Header,
  type Row,
  type RowData,
  type SortingState,
  type Table,
  type TableMeta,
  useVueTable,
  type VisibilityState,
} from "@tanstack/vue-table";
import {
  computed,
  isRef,
  type MaybeRef,
  type MaybeRefOrGetter,
  type Ref,
  shallowRef,
  toValue,
  watch,
} from "vue";

import { createEventListener } from "@/lib/composables/event-listener";
import { useMediaQuery } from "@/lib/composables/media-query";
import { type DataSource, normalizeDataSource } from "@/types/data-source";
import type { GenericModel } from "@/types/model";
import type { Selection } from "@/types/selection";

import { ACTIONS_COLUMN_ID, SELECTION_COLUMN_ID } from "./columns";
import type { PersistedColumnsStore } from "./composables";
import type { SelectionAction } from "./types/selection";

export interface SelectionOptions<T> {
  /** Selection state */
  state: Ref<Selection<T> | undefined>;
  /** Which items are allowed to be selected */
  canSelect?: (item: T, selection: Selection<T> | undefined) => boolean;

  widget?: {
    labels: MaybeRefOrGetter<{ one: string; many: string }>;
    actions:
      | MaybeRef<SelectionAction[]>
      | ((selection: Selection<T>) => SelectionAction[]);
  };
}

interface SelectionMeta<T> extends SelectionOptions<T> {
  lastSelectedId: Ref<string | undefined>;
  shiftKeyHeld: Ref<boolean>;
  hoveringId: Ref<string | undefined>;
  highlightedIds: Ref<Set<string> | undefined>;
}

interface UseTableOptions<T extends GenericModel> {
  source: DataSource<T>;

  sorting?: {
    /** Sorting state */
    state: Ref<string | undefined>;
    /** Attempt client-side sorting */
    clientSorting?: boolean;
    /** Allow unsorted state (after descending, go to unsorted rather than ascending) */
    allowUnsorted?: boolean;
  };
  selection?: SelectionOptions<T>;
  view?: {
    /** Group rows by column */
    groupBy?: MaybeRefOrGetter<string | undefined>;
    /** Column visibility and ordering state */
    columnVisibility?: Ref<PersistedColumnsStore>;
    /** Which row is currently being viewed */
    activeRow?: Ref<string | undefined>;
  };
  /** Custom class(es) to apply to each row based on row data */
  getRowClass?: (row: T) => string | string[] | undefined;

  columns: (ColumnDef<NoInfer<T>, any> | Ref<ColumnDef<NoInfer<T>, any>[]>)[];
}

export const useTable = <T extends GenericModel>(
  options: UseTableOptions<T>
) => {
  const {
    source: rawSource,
    selection,
    sorting,
    view: { groupBy, columnVisibility, activeRow } = {},
    getRowClass,
  } = options;

  const source = normalizeDataSource(rawSource);

  const mqlColumns = shallowRef<VisibilityState>();

  const tableGrouping = computed((): GroupingState | undefined => {
    const value = toValue(groupBy);
    return value ? [value] : undefined;
  });

  const tableSorting = computed((): SortingState | undefined => {
    const state = toValue(sorting?.state);
    if (!state) {
      return;
    }

    return toSortingState(state);
  });

  const tableColumns = computed((): VisibilityState | undefined => {
    const mql = mqlColumns.value;
    const allowed = columnVisibility?.value.columns;

    if (allowed) {
      const columns = instance.getAllColumns();
      const state: VisibilityState = {};
      for (const col of columns) {
        const def = col.columnDef;
        const id = def.id;
        if (
          def.meta?.primary ||
          def.enableHiding === false ||
          (id !== undefined && SYSTEM_COLUMNS.includes(id))
        ) {
          continue;
        }

        if (!allowed.includes(col.id)) {
          state[col.id] = false;
        }
      }

      return { ...state, ...mql };
    }

    return mql;
  });

  const tableColumnOrder = computed((): string[] | undefined => {
    if (!columnVisibility) {
      return undefined;
    }

    const fixedStart: string[] = [];
    const fixedEnd: string[] = [];
    const customizable: string[] = [];

    for (const column of instance.getAllColumns()) {
      const id = column.id;
      const columnDef = column.columnDef;

      // Columns that can't be hidden or have fixed placement should be treated as fixed
      const isFixed =
        isFixedColumn(column) ||
        columnDef.enableHiding === false ||
        columnDef.meta?.fixed !== undefined;

      if (isFixed) {
        const placement = columnDef.meta?.fixed ?? "start";

        if (placement === "start") {
          fixedStart.push(id);
        } else if (placement === "end") {
          fixedEnd.push(id);
        }
      } else {
        customizable.push(id);
      }
    }

    const { columns, reordered } = columnVisibility.value;

    // If not reordered, columns are already in canonical order - just filter by visibility
    const orderedCustomizable = reordered
      ? columns.filter((id) => customizable.includes(id))
      : customizable.filter((id) => columns.includes(id));

    return [...fixedStart, ...orderedCustomizable, ...fixedEnd];
  });

  const resolvedColumns = computed((): ColumnDef<T, any>[] => {
    const definitions = options.columns;

    // Not sure why I can't use either `unref` or `toValue` here
    return definitions.flatMap((def) => (isRef(def) ? def.value : def));
  });

  const instance = useVueTable({
    getRowId: (row) => row.id,
    get data() {
      return source.data;
    },
    get columns() {
      return resolvedColumns.value;
    },

    state: {
      get grouping() {
        return tableGrouping.value;
      },

      get sorting() {
        return tableSorting.value;
      },
      get columnVisibility() {
        return tableColumns.value;
      },
      get columnOrder() {
        return tableColumnOrder.value;
      },
    },
    onSortingChange(updaterOrValue) {
      if (sorting) {
        let next =
          typeof updaterOrValue === "function"
            ? updaterOrValue(tableSorting.value ?? [])
            : updaterOrValue;

        if (!sorting.allowUnsorted && next.length === 0) {
          // Grab from current state and flip
          const current = tableSorting.value;
          if (!current || current.length === 0) {
            return;
          }

          next = [{ id: current[0].id, desc: !current[0].desc }];
        }

        sorting.state.value = fromSortingState(next);
      }
    },

    enableSorting: sorting !== undefined,
    enableRowSelection: selection !== undefined,
    enableHiding: true,

    getCoreRowModel: getCoreRowModel(),

    getSortedRowModel: getSortedRowModel(),
    manualSorting: !sorting?.clientSorting,

    getGroupedRowModel: getGroupedRowModel(),
    groupedColumnMode: "remove",

    meta: {
      selection: selection && createSelectionMeta(selection, source),
      activeRow,
      getRowClass,
    },
  });

  const isEmpty = computed(() => !source.data.length);

  watch(
    useMediaQuery("(min-width: 1024px)"),
    (isOnDesktop) => {
      if (isOnDesktop) {
        mqlColumns.value = undefined;
        return;
      }

      const nextState: VisibilityState = {};
      for (const col of instance.getAllColumns()) {
        const def = col.columnDef;
        if (def.meta?.primary || def.meta?.showOnMobile) {
          continue;
        }

        nextState[col.id] = false;
      }

      mqlColumns.value = nextState;
    },
    { immediate: true }
  );

  return {
    internal: {
      instance,

      // we use `columnVisibility` for media queries, so if we go through
      // tanstack table then anything from `mqlColumns` gets reflected into
      // the persisted user state.
      columnVisibility: columnVisibility,

      source: source,
      get isEmpty() {
        return isEmpty.value;
      },
    },
  };
};

const createSelectionMeta = <T extends { id: string }>(
  selection: SelectionOptions<T>,
  source: { data: T[] }
): SelectionMeta<T> => {
  const shiftKeyHeld = shallowRef(false);
  const hoveringId = shallowRef<string | undefined>();
  const lastSelectedId = shallowRef<string | undefined>();

  const rowIds = computed(() => source.data.map((item) => item.id));
  const highlightedIds = computed((): Set<string> | undefined => {
    const ids = rowIds.value;
    if (ids.length === 0 || !shiftKeyHeld.value) {
      return;
    }

    const a = lastSelectedId.value;
    if (a === undefined) {
      return;
    }

    const b = hoveringId.value;
    if (b === undefined || a === b) {
      return;
    }

    const indexA = ids.indexOf(a);
    const indexB = ids.indexOf(b);

    if (indexA === -1 || indexB === -1) {
      return undefined;
    }

    const start = Math.min(indexA, indexB);
    const end = Math.max(indexA, indexB);

    return new Set(ids.slice(start, end + 1));
  });

  createEventListener(document, "keydown", (ev) => {
    if (ev.shiftKey) {
      shiftKeyHeld.value = true;
    }
  });

  createEventListener(document, "keyup", (ev) => {
    if (!ev.shiftKey) {
      shiftKeyHeld.value = false;
    }
  });

  return {
    state: selection.state,
    canSelect: selection.canSelect,
    widget: selection.widget,
    shiftKeyHeld,
    lastSelectedId,
    hoveringId,
    highlightedIds,
  };
};

export type TableInstance<T extends GenericModel> = ReturnType<
  typeof useTable<T>
>;

export type InternalTableInstance<T extends GenericModel> =
  TableInstance<T>["internal"];

export const isPrimaryColumn = (column: Column<any>): boolean => {
  return !!column.columnDef.meta?.primary;
};

const SYSTEM_COLUMNS = [SELECTION_COLUMN_ID, ACTIONS_COLUMN_ID];

export const isSystemColumn = (column: Column<any>): boolean => {
  return SYSTEM_COLUMNS.includes(column.id);
};

export const isFixedColumn = (column: Column<any>): boolean => {
  return isPrimaryColumn(column) || isSystemColumn(column);
};

// Re-export commonly used tanstack-table utilities
export { createColumnHelper, FlexRender, getExpandedRowModel };

// Re-export commonly used tanstack-table types
export type {
  Cell,
  Column,
  ColumnDef,
  DisplayColumnDef,
  ExpandedState,
  Header,
  Row,
  RowData,
  Table,
  TableMeta,
};

declare module "@tanstack/vue-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    selection?: SelectionMeta<TData>;
    activeRow?: Ref<string | undefined>;
    /** Custom class(es) to apply to each row based on row data */
    getRowClass?: (row: TData) => string | string[] | undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** This is the primary column */
    primary?: boolean;
    /** Always show this column on mobile list view */
    showOnMobile?: boolean;
    /** Fixed column placement (defaults to "start") */
    fixed?: "start" | "end";

    /** Column visibility options */
    visibility?: {
      /** Label shown for this column */
      label?: string;
      /** Which group this column belongs to */
      group?: string;
      /** Whether to show this column in customize options */
      canShow?: MaybeRefOrGetter<unknown>;
    };
    /** Column display options */
    display?: {
      /** Text alignment preference */
      align?: "left" | "right" | "center";
      /** Disable tabular number text styling */
      proportialNums?: boolean;
      /** Allow wrapping */
      wrap?: boolean;
    };

    cellClass?: string;

    /** Row actions factory for context menus */
    actionsRegistrar?: (context: {
      item: TData;
      index: number;
    }) => import("./types/overflow").ActionItem[];
  }
}

const toSortingState = (sort: string): SortingState => {
  const desc = sort.startsWith("-");
  const id = desc ? sort.slice(1) : sort;

  return [{ id, desc }];
};

const fromSortingState = (state: SortingState): string | undefined => {
  if (state.length === 0) {
    return undefined;
  }

  const { id, desc } = state[0];
  return `${desc ? "-" : ""}${id}`;
};
